import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { sowService } from '../../services/sowService';
import { CreateSOWDialog } from '../../components/SOW/CreateSOWDialog';
import { Plus, FileText, Search, Filter, Calendar, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '../../components/ui/badge';

interface SOWRegistryItem {
  id: string;
  address: string;
  status: string;
  date: string;
}

// Simple input component to match PropertyListPage
const Input = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => {
  return (
    <input
      ref={ref}
      className={`flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
    />
  )
})
Input.displayName = "Input"

export const SOWListPage: React.FC = () => {
  const [sows, setSows] = useState<SOWRegistryItem[]>([]);
  const [filteredSOWs, setFilteredSOWs] = useState<SOWRegistryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  
  // Sort options
  const [sortOption, setSortOption] = useState<'newest' | 'oldest' | 'alphabetical'>('newest');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Pagination options
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [pageSizeDropdownOpen, setPageSizeDropdownOpen] = useState(false);
  const pageSizeDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Handle sort dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      // Handle page size dropdown
      if (pageSizeDropdownRef.current && !pageSizeDropdownRef.current.contains(event.target as Node)) {
        setPageSizeDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    loadSOWRegistry();
  }, []);

  // Filter and sort SOWs whenever the search query, sort option, or sows change
  useEffect(() => {
    let filtered = sows.filter(sow =>
      sow.address.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sort the filtered results
    switch (sortOption) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        break;
      case 'alphabetical':
        filtered.sort((a, b) => a.address.localeCompare(b.address));
        break;
    }

    setFilteredSOWs(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [sows, searchQuery, sortOption]);

  const loadSOWRegistry = async () => {
    try {
      setLoading(true);
      const registry = await sowService.getSOWRegistry();
      setSows(registry.sowRegistry || []);
    } catch (error) {
      console.error('Error loading SOW registry:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSOWCreated = (sowId: string) => {
    setIsCreateDialogOpen(false);
    navigate(`/sow/${sowId}/wizard`);
  };

  const handleSOWClick = (sowId: string) => {
    navigate(`/sow/${sowId}`);
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'complete':
        return 'default';
      case 'review':
        return 'secondary';
      case 'estimating':
        return 'outline';
      case 'initialQuestionnaire':
        return 'outline';
      case 'scope-gathering':
        return 'outline';
      case 'draft':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'complete':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'review':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'estimating':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'initialQuestionnaire':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'scope-gathering':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'draft':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  // Calculate pagination info
  const totalPages = Math.ceil(filteredSOWs.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, filteredSOWs.length);
  const currentSOWs = filteredSOWs.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-10 w-10 text-fxer-blue animate-spin mb-4" />
          <p className="text-gray-500">Loading SOWs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex flex-col gap-6">
        {/* Header with Title and Add New Button */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-fxer-blue">Projects</h1>
            <p className="text-gray-500 mt-1">Manage your renovation projects</p>
          </div>
          <Button 
            onClick={() => setIsCreateDialogOpen(true)}
            className="bg-fxer-blue hover:opacity-90"
          >
            <Plus className="mr-2 h-4 w-4" />
            New Project
          </Button>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="relative" ref={dropdownRef}>
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <Filter className="h-4 w-4" />
              <span>Sort: {sortOption === 'newest' ? 'Newest' : sortOption === 'oldest' ? 'Oldest' : 'A-Z'}</span>
            </Button>
            
            {dropdownOpen && (
              <div className="absolute right-0 z-10 mt-2 w-40 rounded-md border border-gray-200 bg-white shadow-lg">
                <div className="p-1">
                  <button
                    className={`flex w-full items-center rounded-sm px-3 py-2 text-sm ${sortOption === 'newest' ? 'bg-gray-100 text-fxer-blue font-medium' : ''}`}
                    onClick={() => {
                      setSortOption('newest');
                      setDropdownOpen(false);
                    }}
                  >
                    Newest First
                  </button>
                  <button
                    className={`flex w-full items-center rounded-sm px-3 py-2 text-sm ${sortOption === 'oldest' ? 'bg-gray-100 text-fxer-blue font-medium' : ''}`}
                    onClick={() => {
                      setSortOption('oldest');
                      setDropdownOpen(false);
                    }}
                  >
                    Oldest First
                  </button>
                  <button
                    className={`flex w-full items-center rounded-sm px-3 py-2 text-sm ${sortOption === 'alphabetical' ? 'bg-gray-100 text-fxer-blue font-medium' : ''}`}
                    onClick={() => {
                      setSortOption('alphabetical');
                      setDropdownOpen(false);
                    }}
                  >
                    Alphabetical
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* SOW List */}
        {filteredSOWs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              {searchQuery ? (
                <>
                  <p className="text-gray-500 mb-2">No projects match your search</p>
                  <Button variant="outline" onClick={() => setSearchQuery('')}>
                    Clear Search
                  </Button>
                </>
              ) : (
                <>
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-medium mb-2">No projects yet</h3>
                  <p className="text-gray-500 mb-6">
                    Create your first project to get started
                  </p>
                  <Button 
                    onClick={() => setIsCreateDialogOpen(true)}
                    className="bg-fxer-blue hover:opacity-90"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Your First Project
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Count display */}
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-gray-500">
                Showing {startIndex + 1} to {endIndex} of {filteredSOWs.length} projects
              </div>
              
              {/* Page size selector */}
              <div className="relative" ref={pageSizeDropdownRef}>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-sm"
                  onClick={() => setPageSizeDropdownOpen(!pageSizeDropdownOpen)}
                >
                  {pageSize} per page
                </Button>
                
                {pageSizeDropdownOpen && (
                  <div className="absolute right-0 z-10 mt-2 w-32 rounded-md border border-gray-200 bg-white shadow-lg">
                    <div className="p-1">
                      {[5, 10, 25, 50].map((size) => (
                        <button
                          key={size}
                          className={`flex w-full items-center rounded-sm px-3 py-2 text-sm ${pageSize === size ? 'bg-gray-100 text-fxer-blue font-medium' : ''}`}
                          onClick={() => {
                            setPageSize(size);
                            setCurrentPage(1); // Reset to first page when changing page size
                            setPageSizeDropdownOpen(false);
                          }}
                        >
                          {size} per page
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          
            {/* SOW cards */}
            <div className="grid grid-cols-1 gap-4">
              {currentSOWs.map((sow) => (
                <Card 
                  key={sow.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleSOWClick(sow.id)}
                >
                  <CardContent className="p-5">
                    <div className="flex justify-between items-start">
                      <div className="flex-grow">
                        <h3 className="text-lg font-medium mb-1">{sow.address}</h3>
                        <div className="flex items-center gap-4 text-gray-500 text-sm">
                          <div className="flex items-center">
                            <Calendar className="h-3.5 w-3.5 mr-1" />
                            <span>Created {new Date(sow.date).toLocaleDateString()}</span>
                          </div>
                          <Badge variant={getStatusBadgeVariant(sow.status)} className={getStatusBadgeClass(sow.status)}>
                            {sow.status}
                          </Badge>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        className="rounded-full h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSOWClick(sow.id);
                        }}
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                
                <span className="text-sm text-gray-600 px-4">
                  Page {currentPage} of {totalPages}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}

        <CreateSOWDialog 
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          onSOWCreated={handleSOWCreated}
        />
      </div>
    </div>
  );
};